import type { IClickmapSorted } from '@gem-sdk/heatmap-react';

import { useHeatmapMetaContext } from './view-context/useHeatmapMetaContext';
import { ESelectorToken } from '../api/useFetchClickedElements';
import { useHeatmapDataContext } from '@gem-sdk/heatmap-react';
import { useMemo } from 'react';

const DEFAULT_SEARCH_TOKENS: ESelectorToken[] = [
  ESelectorToken.TEXT,
  ESelectorToken.ID,
  ESelectorToken.CLASS,
  ESelectorToken.TAG,
];

/**
 * Normalize class string for comparison
 * - "btn btn-primary" -> "btn btn-primary"
 * - ".btn.btn-primary" -> "btn btn-primary"
 */
const normalizeClassString = (value: string): string => {
  if (value.startsWith('.')) {
    return value.slice(1).replace(/\./g, ' ');
  }
  return value;
};

const matchesSearch = (
  element: IClickmapSorted,
  query: string,
  searchTokens: ESelectorToken[] = DEFAULT_SEARCH_TOKENS,
): boolean => {
  if (!query.trim()) return true;

  const normalizedQuery = query.toLowerCase().trim();
  const listSelectors = element.listSelectors;

  if (!listSelectors || listSelectors.length === 0) return false;

  return searchTokens.some((token) => {
    const value = listSelectors[token];
    if (!value) return false;

    const normalizedValue = value.toLowerCase();

    // ID search: user can type "#myId" or "myId"
    if (token === ESelectorToken.ID) {
      const queryWithoutHash = normalizedQuery.startsWith('#')
        ? normalizedQuery.slice(1)
        : normalizedQuery;
      return normalizedValue.includes(queryWithoutHash);
    }

    // Class search: ".btn.primary" or ".btn" uses per-class matching; plain text uses substring
    if (token === ESelectorToken.CLASS) {
      if (normalizedQuery.startsWith('.')) {
        const normalizedClassQuery = normalizeClassString(normalizedQuery);
        const queryClasses = normalizedClassQuery.split(/\s+/).filter(Boolean);
        const valueClasses = normalizedValue.split(/\s+/).filter(Boolean);

        return queryClasses.every((qClass) =>
          valueClasses.some((vClass) => vClass.includes(qClass)),
        );
      }

      return normalizedValue.includes(normalizedQuery);
    }

    return normalizedValue.includes(normalizedQuery);
  });
};

export const useHeatmapClickRankSearch = () => {
  const searchQuery = useHeatmapMetaContext((s) => s.rankQuery);
  const sortedElements = useHeatmapDataContext((s) => s.dataInfo?.sortedElements);

  const filteredElements = useMemo(() => {
    if (!sortedElements) return [];
    if (!searchQuery.trim()) return sortedElements;

    return sortedElements.filter((element) => matchesSearch(element, searchQuery));
  }, [sortedElements, searchQuery]);

  const isEmptySearch = useMemo(() => {
    return searchQuery.trim() && filteredElements.length === 0;
  }, [searchQuery, filteredElements]);

  return {
    filteredElements,
    isEmptySearch,
  };
};
